// Stage-aware outreach templates for deals.
//
// These build the prefilled Email (Gmail compose) and WhatsApp (wa.me) drafts.
// Everything is client-side: we just open the external app with the draft —
// no SendGrid/Twilio credentials required.

import { fmtCompactINR } from "@/lib/api";

const COMPANY = "Zalani Paper Mart";

// Per-stage email subject + body and whatsapp body. `ctx` carries the resolved
// fields so each template stays a pure string builder.
function templatesFor(ctx) {
  const { contact, company, title, fobValue, cifValue, exworksValue, senderName } = ctx;
  const greeting = `Hi ${contact || "there"},`;
  const sign = `\n\nBest regards,\n${senderName || "Sales"}\n${COMPANY}`;
  const deal = title || "your enquiry";
  const valLine = (fobValue || cifValue || exworksValue)
    ? ` (FOB ${fmtCompactINR(fobValue)}, CIF ${fmtCompactINR(cifValue)}, Ex-Works ${fmtCompactINR(exworksValue)})`
    : "";

  const map = {
    lead: {
      subject: `${company} × ${COMPANY} — your paper requirement`,
      body: `${greeting}\n\nThank you for your interest in ${COMPANY}. We'd love to understand your requirement for "${deal}" better and share how we can help.\n\nWould you have a few minutes this week for a quick call?${sign}`,
    },
    quoted: {
      subject: `Quotation for ${deal} — ${COMPANY}`,
      body: `${greeting}\n\nPlease find our quotation for "${deal}"${valLine}. Do let us know if you have any questions — we're happy to adjust quantities or specifications.\n\nLooking forward to your feedback.${sign}`,
    },
    sample_sent: {
      subject: `Sample dispatched for ${deal} — ${COMPANY}`,
      body: `${greeting}\n\nWe've dispatched the sample for "${deal}". Once it reaches you, we'd appreciate your feedback on the quality and finish.\n\nHappy to proceed with a bulk order whenever you're ready.${sign}`,
    },
    approved: {
      subject: `Confirming next steps for ${deal} — ${COMPANY}`,
      body: `${greeting}\n\nThank you for approving "${deal}"${valLine}. To move ahead, please confirm the final quantity and delivery address, and we'll raise the order.\n\nWe appreciate your trust in ${COMPANY}.${sign}`,
    },
    won: {
      subject: `Thank you for your order — ${COMPANY}`,
      body: `${greeting}\n\nThank you for your order for "${deal}"! We're processing it now and will keep you posted on dispatch.\n\nIt's a pleasure working with ${company}, and we look forward to a long association.${sign}`,
    },
    lost: {
      subject: `Staying in touch — ${COMPANY}`,
      body: `${greeting}\n\nWe understand "${deal}" didn't work out this time, and that's completely alright. We'd be glad to be considered for your future paper requirements.\n\nDo keep us in mind — wishing you all the best.${sign}`,
    },
  };

  return map[ctx.stage] || map.lead;
}

// Resolve the customer + deal + sender into the fields templates need.
function buildCtx(deal, customer, sender) {
  return {
    stage: deal?.stage || "lead",
    contact: customer?.contact || "",
    company: deal?.customer_name || customer?.company || "",
    title: deal?.title || "",
    fobValue: deal?.fob_value || 0,
    cifValue: deal?.cif_value || 0,
    exworksValue: deal?.exworks_value || 0,
    senderName: sender?.name || "",
  };
}

export function buildEmailDraft(deal, customer, sender) {
  const ctx = buildCtx(deal, customer, sender);
  const t = templatesFor(ctx);
  return {
    to: customer?.emails?.[0] || "",
    subject: t.subject,
    body: t.body,
  };
}

// Follow-up (nudge) templates — used by the "Follow up" CTA to schedule a
// stage-aware reminder. The tone here is a gentle check-in *after* a step has
// happened (quote sent, samples sent, approved…), distinct from the first-touch
// outreach templates above. Every pipeline stage is covered.
function followupTemplatesFor(ctx) {
  const { contact, company, title, senderName } = ctx;
  const greeting = `Hi ${contact || "there"},`;
  const sign = `\n\nBest regards,\n${senderName || "Sales"}\n${COMPANY}`;
  const deal = title || "your requirement";

  const map = {
    lead: {
      subject: `Following up · ${company}`,
      body: `${greeting}\n\nJust following up on your enquiry for "${deal}". Have you had a chance to consider it? We'd be glad to share pricing or our catalogue whenever you're ready.${sign}`,
    },
    quoted: {
      subject: `Following up on our quotation · ${company}`,
      body: `${greeting}\n\nI wanted to follow up on the quotation we shared for "${deal}". Did you get a chance to review it? We're happy to revise quantities, specifications or terms if that helps.${sign}`,
    },
    sample_sent: {
      subject: `How were the samples? · ${company}`,
      body: `${greeting}\n\nFollowing up on the samples we dispatched for "${deal}". We'd love your feedback on the quality and finish, and are happy to proceed with a bulk order whenever you're ready.${sign}`,
    },
    approved: {
      subject: `Next steps on your approval · ${company}`,
      body: `${greeting}\n\nThank you for approving "${deal}". To move ahead, could you please confirm the final quantity and delivery details so we can raise the order?${sign}`,
    },
    not_approved: {
      subject: `Another look at ${deal}? · ${company}`,
      body: `${greeting}\n\nI understand our last proposal for "${deal}" didn't quite work out. We'd welcome the chance to revisit it with revised terms or alternatives — would you be open to that?${sign}`,
    },
    won: {
      subject: `Checking in on your order · ${company}`,
      body: `${greeting}\n\nJust checking in on your order for "${deal}" to make sure everything is on track. Do let us know if there's anything you need — and please keep us in mind for your next requirement.${sign}`,
    },
    lost: {
      subject: `Staying in touch · ${company}`,
      body: `${greeting}\n\nCircling back regarding "${deal}". If anything has changed, or a new requirement comes up, we'd be glad to help. Wishing you all the best.${sign}`,
    },
  };

  return map[ctx.stage] || map.lead;
}

// Draft a stage-aware follow-up to schedule via the Follow-ups queue.
// `channel` picks the default recipient (phone vs email); the body is plain
// text and identical across channels (WhatsApp is plain text; email is wrapped
// server-side when sent).
export function buildFollowupDraft(deal, customer, sender, channel = "whatsapp") {
  const ctx = buildCtx(deal, customer, sender);
  const t = followupTemplatesFor(ctx);
  const to = channel === "email" ? customer?.emails?.[0] || "" : customer?.phones?.[0] || "";
  return { to, subject: t.subject, body: t.body };
}

export function buildWhatsappDraft(deal, customer, sender) {
  const ctx = buildCtx(deal, customer, sender);
  const t = templatesFor(ctx);
  // WhatsApp is plain text; reuse the email body (already plain text).
  return {
    to: customer?.phones?.[0] || "",
    body: t.body,
  };
}

// Build a Gmail draft (to/subject/body) summarising a saved quotation.
// `q` is the persisted quote (carries quote_no, currency_symbol, totals, items,
// valid_until). Kept plain-text so it drops straight into Gmail compose.
// Plain-text product-introduction email for a customer (no quote attached).
// Personalized by greeting (contact name when known, else "Sir/Madam") and
// by sender sign-off; the rest of the copy is the standard ZPAQ pitch used
// for both single-customer and bulk sends from the Customers page.
export function buildCustomerEmailDraft(customer, sender) {
  const greeting = customer?.contact ? `Dear ${customer.contact},` : "Dear Sir/Madam,";
  const senderName = sender?.name || "Sales";
  const body =
    `${greeting}\n\n` +
    `Greetings from ZPAQ, a brand by Zalani Papers, India.\n\n` +
    `With over 60 years of industry experience, we manufacture premium poly-coated packaging materials at our state-of-the-art facility in Kandla SEZ, Gujarat.\n\n` +
    `Our Product Range includes:\n\n` +
    `* Food Grade, Brown & Ribbed Kraft Papers + PE Coating\n` +
    `* Chromo Paper + PE Coating\n` +
    `* Foil Laminates (MGBK, Honeycomb & Chocolate Foil)\n` +
    `* Sack Kraft & Stiffener Board + PE Coating\n` +
    `* Baking Paper & Greaseproof Paper in Sheets and Rolls\n\n` +
    `✅ All our products are FSC certified.\n\n` +
    `We're also excited to announce the latest additions to our business:\n\n` +
    `* Flexographic printing for custom-branded Aluminium Foil Paper, Greaseproof Paper, Honeycomb Paper & Wax Paper.\n` +
    `* Cut-to-size sheets packed in cartons.\n` +
    `* Foil Pop-Up Sheets and Foil Paper Pop-Up Sheets.\n` +
    `* Ready-to-retail products with private labelling, including Aluminium Foil Rolls, Foil Paper Rolls, Food Wrapping Paper Rolls, Baking Paper Rolls and Wax Paper Rolls.\n\n` +
    `Please find the attached product images for your reference.\n\n` +
    `We would be happy to share our complete catalogue and discuss your packaging requirements. We would be pleased to discuss this further if it interests you.\n\n` +
    `We look forward to hearing from you.\n\n` +
    `Best regards,\n${senderName}\nZPAQ · Zalani Papers, India`;
  return {
    to: customer?.emails?.[0] || "",
    subject: "Upgrade Your Packaging — Premium Kraft, Foil & Baking Papers, Private Labelled",
    body,
  };
}

// Plain-text introduction email for a supplier/vendor contact — distinct from
// buildCustomerEmailDraft, which pitches ZPAQ products to a prospective buyer.
export function buildSupplierEmailDraft(supplier, sender) {
  const greeting = supplier?.contact ? `Dear ${supplier.contact},` : "Dear Sir/Madam,";
  const senderName = sender?.name || "Sales";
  const body =
    `${greeting}\n\n` +
    `This is ${senderName} from ${COMPANY}. We wanted to reach out regarding our ongoing sourcing requirements and explore how we can work together.\n\n` +
    `Please let us know a good time to connect, or share your latest catalogue and pricing.\n\n` +
    `Best regards,\n${senderName}\n${COMPANY}`;
  return {
    to: supplier?.emails?.[0] || "",
    subject: `Sourcing enquiry — ${COMPANY} × ${supplier?.company || ""}`,
    body,
  };
}

export function buildQuoteEmailDraft(q, customer, sender) {
  const name = customer?.contact || customer?.company || "there";
  const company = customer?.company || "your company";
  const sym = q?.currency_symbol || "";
  const money = (n) =>
    sym + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const showExw = q?.show_exworks !== false;

  const lines = (q?.items || []).map((it, i) => {
    const parts = [
      `${i + 1}. ${it.product || "—"} — ${it.quantity} ${it.unit || ""}`.trim(),
      `FOB ${money(it.fob_price)}`,
      `CIF ${money(it.cif_price)}`,
      ...(showExw ? [`Ex-Works ${money(it.ex_works_price)}`] : []),
    ];
    return parts.join("  ·  ");
  });

  const totals = [
    `FOB Total: ${money(q?.fob_total)}`,
    `CIF Total: ${money(q?.cif_total)}`,
    ...(showExw ? [`Ex-Works Total: ${money(q?.exworks_total)}`] : []),
    ...((q?.tax_rate || 0) > 0 ? [`${q?.tax_label || "GST"}: ${money(q?.tax_amount)}`] : []),
    ...((q?.tax_rate || 0) > 0 ? [`Grand Total: ${money(q?.grand_total)}`] : []),
  ];

  const body =
    `Dear ${name},\n\n` +
    `Thank you for your interest. Please find our quotation ${q?.quote_no} for ${company} below.\n\n` +
    `${lines.join("\n")}\n\n` +
    `${totals.join("\n")}\n\n` +
    (q?.valid_until ? `Valid until: ${q.valid_until}\n\n` : "") +
    `Please let us know if you have any questions.\n\n` +
    `Best regards,\n${sender?.name || "Sales"}\n${COMPANY}`;

  return {
    to: customer?.emails?.[0] || "",
    subject: `Quotation ${q?.quote_no} from ${COMPANY}`,
    body,
  };
}

// Gmail compose URL — opens a new compose window with fields prefilled.
export function gmailComposeUrl({ to, subject, body }) {
  const p = new URLSearchParams({ view: "cm", fs: "1", to: to || "", su: subject || "", body: body || "" });
  return `https://mail.google.com/mail/?${p.toString()}`;
}

// wa.me click-to-chat URL. Strips everything but digits from the phone.
export function whatsappUrl({ to, body }) {
  const digits = String(to || "").replace(/\D/g, "");
  return `https://wa.me/${digits}?text=${encodeURIComponent(body || "")}`;
}
